import { createContext, useContext, useMemo } from "react";
import { useGetMyPermissionsQuery } from "../features/user/userApi.services";
import { isAuthenticated } from "../routes/guards/authUtils";
import {
  can as canCheck,
  canViewModule as canViewModuleCheck,
  checkRequirements,
  hasPermission as hasPermissionCheck,
  normalizePermissions,
} from "./utils";

const PermissionsContext = createContext(null);

const EMPTY_PERMISSIONS = Object.freeze({
  permissions: Object.freeze({}),
  codenames: Object.freeze([]),
  userId: null,
  email: null,
  role: null,
  roleLabel: null,
  isLoading: false,
  isError: false,
  refetch: () => {},
  hasPermission: () => false,
  can: () => false,
  canViewModule: () => false,
  canAny: () => false,
  canAll: () => false,
});

/**
 * Loads `/api/me/permissions/` for authenticated users and exposes helpers.
 * Mount inside the private (authenticated) route tree.
 */
export function PermissionsProvider({ children }) {
  const authenticated = isAuthenticated();
  const { data, isLoading, isFetching, isError, refetch } =
    useGetMyPermissionsQuery(undefined, {
      skip: !authenticated,
      refetchOnMountOrArgChange: true,
    });

  const value = useMemo(() => {
    const permissions = normalizePermissions(data);
    const codenames = Object.entries(permissions)
      .filter(([, granted]) => granted === true)
      .map(([codename]) => codename);

    return {
      permissions,
      codenames,
      userId: data?.user_id ?? null,
      email: data?.email ?? null,
      role: data?.role ?? null,
      roleLabel: data?.role_label ?? null,
      isLoading: authenticated && (isLoading || isFetching),
      isError,
      refetch,
      hasPermission: (codename) => hasPermissionCheck(permissions, codename),
      can: (module, action) => canCheck(permissions, module, action),
      canViewModule: (module) => canViewModuleCheck(permissions, module),
      canAny: (requirements) =>
        checkRequirements(permissions, requirements, "any"),
      canAll: (requirements) =>
        checkRequirements(permissions, requirements, "all"),
    };
  }, [authenticated, data, isLoading, isFetching, isError, refetch]);

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * Returns permission helpers. Falls back to empty grants outside the provider
 * (e.g. during logout redirect) so layout shells do not crash.
 */
export function usePermissions() {
  return useContext(PermissionsContext) ?? EMPTY_PERMISSIONS;
}

/**
 * Safe variant that returns `null` when outside the provider.
 */
export function usePermissionsOptional() {
  return useContext(PermissionsContext);
}
